import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const humanize = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const Stat = ({ label, value, color, testid }) => (
  <div className={`rounded-xl p-3 text-center ${color}`} data-testid={testid}>
    <p className="text-2xl font-bold leading-tight">{value}</p>
    <p className="text-[11px] opacity-80 mt-0.5">{label}</p>
  </div>
);

const Field = ({ label, value }) => {
  if (value === null || value === undefined || value === '' || (typeof value === 'object')) return null;
  return (
    <div className="py-1.5 border-b border-slate-100 flex justify-between gap-3">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="text-xs font-medium text-slate-800 text-right">{String(value)}</span>
    </div>
  );
};

const Section = ({ title, children, testid }) => (
  <div className="bg-white rounded-xl shadow-sm p-4" data-testid={testid}>
    <p className="text-sm font-bold text-slate-700 mb-3">{title}</p>
    {children}
  </div>
);

const Avatar = ({ name, photo }) => {
  const initials = (name || '?').split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  if (photo) return <img src={photo} alt={name} className="w-10 h-10 rounded-full object-cover" />;
  return (
    <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold">
      {initials}
    </div>
  );
};

/* ---------------- Activity Timeline Tab ---------------- */
const formatTime = (ts) => {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    return d.toLocaleString('hi-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (e) { return String(ts).slice(0, 16); }
};

const TYPE_STYLE = {
  login: 'bg-blue-50 text-blue-600 ring-blue-100',
  password_reset: 'bg-amber-50 text-amber-600 ring-amber-100',
  payment: 'bg-green-50 text-green-600 ring-green-100',
  upload: 'bg-purple-50 text-purple-600 ring-purple-100',
};

const FILTERS = [
  { id: '', label: 'सभी' },
  { id: 'login', label: '🔐 Logins' },
  { id: 'payment', label: '💰 Payments' },
  { id: 'upload', label: '📤 Uploads' },
  { id: 'password_reset', label: '🔑 Resets' },
];

const ActivityTab = ({ schoolId, authToken }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 40;

  const fetchTimeline = useCallback(async (type, pg) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: '365', page: String(pg), limit: String(limit) });
      if (type) params.set('event_type', type);
      const res = await fetch(`${API_URL}/api/schools/${schoolId}/activity-timeline?${params}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const d = await res.json();
      setEvents(d.events || []);
      setTotal(d.total || 0);
    } catch (e) {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [schoolId, authToken]);

  useEffect(() => { fetchTimeline(filter, page); }, [filter, page, fetchTimeline]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-3" data-testid="school360-activity-tab">
      <div className="flex flex-wrap gap-2" data-testid="activity-filters">
        {FILTERS.map((f) => (
          <button
            key={f.id || 'all'}
            onClick={() => { setFilter(f.id); setPage(1); }}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${filter === f.id ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'}`}
            data-testid={`activity-filter-${f.id || 'all'}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-center text-slate-400 text-sm py-6" data-testid="activity-loading">⏳ लोड हो रहा है...</p>
      ) : events.length === 0 ? (
        <p className="text-center text-slate-400 text-sm py-6" data-testid="activity-empty">कोई गतिविधि नहीं मिली।</p>
      ) : (
        <div className="bg-white rounded-xl shadow-sm p-4" data-testid="activity-timeline">
          <p className="text-[11px] text-slate-400 mb-3">{total} गतिविधियाँ</p>
          <div className="relative pl-6">
            <div className="absolute left-2 top-1 bottom-1 w-px bg-slate-200" />
            <div className="space-y-4">
              {events.map((e, i) => (
                <div key={i} className="relative" data-testid={`activity-event-${i}`}>
                  <div className={`absolute -left-[18px] top-0.5 w-7 h-7 rounded-full flex items-center justify-center text-xs ring-4 ring-white ${TYPE_STYLE[e.type] || 'bg-slate-50'}`}>
                    {e.icon}
                  </div>
                  <div className="pl-2">
                    <p className="text-sm font-medium text-slate-800 leading-snug">{e.title}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      {[e.subtitle, formatTime(e.timestamp)].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-1" data-testid="activity-pagination">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40"
            data-testid="activity-prev"
          >← पिछला</button>
          <span className="text-xs text-slate-500">{page} / {totalPages}</span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40"
            data-testid="activity-next"
          >अगला →</button>
        </div>
      )}
    </div>
  );
};

/* ---------------- Detail Drawer ---------------- */
const DetailDrawer = ({ open, onClose, title, children, testid }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid={testid}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} data-testid="detail-drawer-overlay" />
      <div className="relative w-full max-w-md bg-white h-full shadow-2xl overflow-y-auto animate-in slide-in-from-right">
        <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-blue-600 text-white p-4 flex items-center justify-between">
          <p className="font-bold text-sm">{title}</p>
          <button onClick={onClose} className="text-white/90 hover:text-white text-xl leading-none" data-testid="detail-drawer-close">×</button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};

/* ---------------- Faculty Tab ---------------- */
const FacultyTab = ({ schoolId, authToken }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const fetchList = useCallback(async (q) => {
    setLoading(true);
    try {
      const url = `${API_URL}/api/schools/${schoolId}/faculty${q ? `?search=${encodeURIComponent(q)}` : ''}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${authToken}` } });
      const d = await res.json();
      setList(d.faculty || []);
    } catch (e) {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [schoolId, authToken]);

  useEffect(() => { fetchList(''); }, [fetchList]);

  useEffect(() => {
    const t = setTimeout(() => fetchList(search), 350);
    return () => clearTimeout(t);
  }, [search, fetchList]);

  return (
    <div className="space-y-3" data-testid="school360-faculty-tab">
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="🔍 नाम / Employee ID / विषय से खोजें..."
        className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-300 outline-none"
        data-testid="faculty-search-input"
      />
      {loading ? (
        <p className="text-center text-slate-400 text-sm py-6" data-testid="faculty-loading">⏳ लोड हो रहा है...</p>
      ) : list.length === 0 ? (
        <p className="text-center text-slate-400 text-sm py-6" data-testid="faculty-empty">कोई शिक्षक/स्टाफ नहीं मिला।</p>
      ) : (
        <div className="space-y-2" data-testid="faculty-list">
          <p className="text-[11px] text-slate-400">{list.length} शिक्षक/स्टाफ</p>
          {list.map((t) => (
            <button
              key={t.id || t.employee_id}
              onClick={() => setSelected(t)}
              className="w-full text-left bg-white rounded-xl shadow-sm p-3 flex items-center gap-3 hover:shadow-md transition-shadow"
              data-testid={`faculty-row-${t.id || t.employee_id}`}
            >
              <Avatar name={t.name} photo={t.photo_url} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{t.name}</p>
                <p className="text-[11px] text-slate-500 truncate">
                  {[t.designation, t.specialization, t.employee_id].filter(Boolean).join(' · ')}
                </p>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${t.is_active === false ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'}`}>
                {t.is_active === false ? 'Inactive' : 'Active'}
              </span>
            </button>
          ))}
        </div>
      )}

      <DetailDrawer open={!!selected} onClose={() => setSelected(null)} title={selected?.name || 'शिक्षक'} testid="faculty-detail-drawer">
        {selected && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Avatar name={selected.name} photo={selected.photo_url} />
              <div>
                <p className="text-sm font-bold text-slate-800">{selected.name}</p>
                <p className="text-xs text-slate-500">{selected.designation || 'Teacher'}</p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <Field label="Employee ID" value={selected.employee_id} />
              <Field label="Phone" value={selected.phone} />
              <Field label="Email" value={selected.email} />
              <Field label="Gender" value={selected.gender} />
              <Field label="Qualification" value={selected.qualification} />
              <Field label="Specialization" value={selected.specialization} />
              <Field label="Experience (yrs)" value={selected.experience_years} />
              <Field label="Subjects" value={Array.isArray(selected.subjects) ? selected.subjects.join(', ') : selected.subjects} />
            </div>
          </div>
        )}
      </DetailDrawer>
    </div>
  );
};

/* ---------------- Students Tab ---------------- */
const StudentsTab = ({ schoolId, authToken, hierarchy }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [selected, setSelected] = useState(null);

  const selectedClass = hierarchy.find((c) => c.id === classId);
  const sections = selectedClass ? selectedClass.sections : [];

  const fetchList = useCallback(async (q, cId, sId) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('search', q);
      if (cId) params.set('class_id', cId);
      if (sId) params.set('section_id', sId);
      const url = `${API_URL}/api/schools/${schoolId}/students${params.toString() ? `?${params}` : ''}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${authToken}` } });
      const d = await res.json();
      setList(d.students || []);
    } catch (e) {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [schoolId, authToken]);

  useEffect(() => {
    const t = setTimeout(() => fetchList(search, classId, sectionId), 350);
    return () => clearTimeout(t);
  }, [search, classId, sectionId, fetchList]);

  const classMap = {};
  hierarchy.forEach((c) => {
    classMap[c.id] = c.name;
    c.sections.forEach((s) => { classMap[s.id] = s.name; });
  });

  return (
    <div className="space-y-3" data-testid="school360-students-tab">
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="🔍 नाम / Admission No / Roll No से खोजें..."
        className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-300 outline-none"
        data-testid="students-search-input"
      />
      <div className="flex gap-2">
        <select
          value={classId}
          onChange={(e) => { setClassId(e.target.value); setSectionId(''); }}
          className="flex-1 px-2 py-2 text-xs rounded-lg border border-slate-200 outline-none"
          data-testid="students-class-filter"
        >
          <option value="">सभी कक्षाएँ</option>
          {hierarchy.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select
          value={sectionId}
          onChange={(e) => setSectionId(e.target.value)}
          disabled={!classId}
          className="flex-1 px-2 py-2 text-xs rounded-lg border border-slate-200 outline-none disabled:bg-slate-50 disabled:text-slate-300"
          data-testid="students-section-filter"
        >
          <option value="">सभी सेक्शन</option>
          {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      {loading ? (
        <p className="text-center text-slate-400 text-sm py-6" data-testid="students-loading">⏳ लोड हो रहा है...</p>
      ) : list.length === 0 ? (
        <p className="text-center text-slate-400 text-sm py-6" data-testid="students-empty">कोई विद्यार्थी नहीं मिला।</p>
      ) : (
        <div className="space-y-2" data-testid="students-list">
          <p className="text-[11px] text-slate-400">{list.length} विद्यार्थी</p>
          {list.map((s) => (
            <button
              key={s.id || s.admission_number}
              onClick={() => setSelected(s)}
              className="w-full text-left bg-white rounded-xl shadow-sm p-3 flex items-center gap-3 hover:shadow-md transition-shadow"
              data-testid={`student-row-${s.id || s.admission_number}`}
            >
              <Avatar name={s.name} photo={s.photo_url} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{s.name}</p>
                <p className="text-[11px] text-slate-500 truncate">
                  {[classMap[s.current_class_id], classMap[s.current_section_id] && `Sec ${classMap[s.current_section_id]}`, `Adm: ${s.admission_number}`].filter(Boolean).join(' · ')}
                </p>
              </div>
              {s.roll_number && <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">Roll {s.roll_number}</span>}
            </button>
          ))}
        </div>
      )}

      <DetailDrawer open={!!selected} onClose={() => setSelected(null)} title={selected?.name || 'विद्यार्थी'} testid="student-detail-drawer">
        {selected && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Avatar name={selected.name} photo={selected.photo_url} />
              <div>
                <p className="text-sm font-bold text-slate-800">{selected.name}</p>
                <p className="text-xs text-slate-500">{[classMap[selected.current_class_id], classMap[selected.current_section_id]].filter(Boolean).join(' - ')}</p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <Field label="Admission No" value={selected.admission_number} />
              <Field label="Roll No" value={selected.roll_number} />
              <Field label="Scholar No" value={selected.scholar_number} />
              <Field label="Gender" value={selected.gender} />
              <Field label="Category" value={selected.caste_category} />
              <Field label="Medium" value={selected.medium} />
              <Field label="Academic Year" value={selected.academic_year} />
              <Field label="Samagra ID" value={selected.samagra_id} />
              <Field label="BPL" value={selected.is_bpl ? 'Yes' : null} />
              <Field label="Minority" value={selected.is_minority ? 'Yes' : null} />
              <Field label="CWSN" value={selected.is_cwsn ? 'Yes' : null} />
            </div>
          </div>
        )}
      </DetailDrawer>
    </div>
  );
};

/* ---------------- Overview ---------------- */
const OverviewTab = ({ data }) => {
  const { school = {}, udise = {}, profile = {}, hierarchy = [], counts = {}, demographics = {} } = data;
  const g = demographics.gender || {};
  const basic = profile.basic_info || {};
  const contact = profile.contact_info || {};

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Students" value={counts.students ?? 0} color="bg-blue-50 text-blue-700" testid="stat-students" />
        <Stat label="Faculty/Staff" value={counts.teachers ?? 0} color="bg-green-50 text-green-700" testid="stat-teachers" />
        <Stat label="Classes" value={counts.classes ?? 0} color="bg-purple-50 text-purple-700" testid="stat-classes" />
        <Stat label="Sections" value={counts.sections ?? 0} color="bg-amber-50 text-amber-700" testid="stat-sections" />
      </div>

      <Section title="📊 Student Demographics" testid="school360-demographics">
        <div className="flex flex-wrap gap-2 text-xs">
          {Object.entries(g).map(([k, v]) => (
            <span key={k} className="px-3 py-1 rounded-full bg-slate-100 text-slate-700">{k}: <b>{v}</b></span>
          ))}
          <span className="px-3 py-1 rounded-full bg-orange-50 text-orange-700">BPL: <b>{demographics.bpl ?? 0}</b></span>
          <span className="px-3 py-1 rounded-full bg-teal-50 text-teal-700">Minority: <b>{demographics.minority ?? 0}</b></span>
        </div>
      </Section>

      {Object.keys(udise).length > 0 ? (
        <Section title="🏛️ UDISE+ Profile (Official)" testid="school360-udise">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
            {Object.entries(udise)
              .filter(([k]) => !['updated_at', 'created_at', 'updated_by', 'school_id'].includes(k))
              .map(([k, v]) => <Field key={k} label={humanize(k)} value={v} />)}
          </div>
        </Section>
      ) : (
        <Section title="🏛️ UDISE+ Profile" testid="school360-udise-empty">
          <p className="text-xs text-slate-400">इस school का UDISE+ डेटा अभी extract नहीं हुआ है।</p>
        </Section>
      )}

      {(Object.keys(basic).length > 0 || Object.keys(contact).length > 0 || school.email || school.address) && (
        <Section title="📞 Contact & Basic Info" testid="school360-contact">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
            <Field label="Principal" value={school.principal_name || basic.principal_name} />
            <Field label="Phone" value={school.phone || contact.phone} />
            <Field label="Email" value={school.email || contact.email} />
            <Field label="Address" value={school.address || contact.address} />
            <Field label="Board" value={school.board || basic.board} />
            <Field label="Medium" value={school.medium || basic.medium} />
            <Field label="Website" value={contact.website} />
            <Field label="Account State" value={school.account_state} />
          </div>
        </Section>
      )}

      <Section title="🏫 Class & Section Hierarchy" testid="school360-hierarchy">
        {hierarchy.length === 0 ? (
          <p className="text-xs text-slate-400">अभी कोई class/section नहीं जोड़ी गई।</p>
        ) : (
          <div className="space-y-2">
            {hierarchy.map((c) => (
              <div key={c.id} className="border border-slate-100 rounded-lg p-2" data-testid={`class-row-${c.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-700">{c.name}</span>
                  <span className="text-[11px] text-slate-500">{c.student_count} students · {c.sections.length} sections</span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {c.sections.map((s) => (
                    <span key={s.id} className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                      {c.name}-{s.name}: {s.student_count}/{s.capacity}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
};

/* ---------------- Profile Edit / Verify Tab (UDISE+ field order) ---------------- */
const UDISE_SECTIONS = [
  { title: '🏫 School Identity', fields: [
    { key: 'udise_code', label: 'UDISE+ Code' },
    { key: 'name', label: 'School Name' },
    { key: 'name_hindi', label: 'School Name (Hindi)' },
    { key: 'board', label: 'Board' },
    { key: 'school_category', label: 'School Category' },
    { key: 'school_management', label: 'Management' },
    { key: 'school_type', label: 'School Type' },
    { key: 'coed_type', label: 'Co-ed Type' },
    { key: 'established_year', label: 'Year of Foundation' },
    { key: 'academic_year', label: 'Academic Year' },
  ]},
  { title: '📍 Location', fields: [
    { key: 'state', label: 'State' },
    { key: 'district', label: 'District' },
    { key: 'block', label: 'Block' },
    { key: 'cluster', label: 'Cluster' },
    { key: 'village', label: 'Village' },
    { key: 'ward_name', label: 'Ward Name' },
    { key: 'urban_local_body', label: 'Urban Local Body' },
    { key: 'address', label: 'Address' },
    { key: 'pincode', label: 'Pincode' },
    { key: 'school_location', label: 'Location (Rural/Urban)' },
    { key: 'latitude', label: 'Latitude' },
    { key: 'longitude', label: 'Longitude' },
  ]},
  { title: '👤 Head & Contact', fields: [
    { key: 'principal_name', label: 'Principal / Head Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
  ]},
  { title: '📚 Academic', fields: [
    { key: 'medium_of_instruction', label: 'Medium of Instruction' },
    { key: 'class_from', label: 'Class From' },
    { key: 'class_to', label: 'Class To' },
  ]},
  { title: '👥 Enrollment', fields: [
    { key: 'total_students', label: 'Total Students', type: 'number' },
    { key: 'total_boys', label: 'Total Boys', type: 'number' },
    { key: 'total_girls', label: 'Total Girls', type: 'number' },
    { key: 'total_teachers', label: 'Total Teachers', type: 'number' },
    { key: 'teachers_regular', label: 'Regular Teachers', type: 'number' },
    { key: 'teachers_contract', label: 'Contract Teachers', type: 'number' },
  ]},
];

const ProfileEditTab = ({ schoolId, authToken, school = {}, udise = {} }) => {
  const seed = () => {
    const out = {};
    UDISE_SECTIONS.forEach((sec) => sec.fields.forEach((f) => {
      let v = school[f.key];
      if (v === undefined || v === null || v === '') v = udise[f.key];
      if (Array.isArray(v)) v = v.join(', ');
      out[f.key] = v === undefined || v === null ? '' : String(v);
    }));
    return out;
  };
  const [form, setForm] = useState(seed);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [udiseInput, setUdiseInput] = useState(String(school.udise_code || udise.udise_code || ''));
  const [looking, setLooking] = useState(false);
  const [udiseFields, setUdiseFields] = useState(() => new Set(school.udise_imported_keys || udise.udise_imported_keys || []));
  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const toStr = (v) => (Array.isArray(v) ? v.join(', ') : (v === undefined || v === null ? '' : String(v)));

  const autofill = async () => {
    const code = (udiseInput || '').trim();
    if (!/^\d{11}$/.test(code)) { setMsg({ ok: false, text: 'UDISE+ code ठीक 11 अंकों का होना चाहिए / must be 11 digits' }); return; }
    setLooking(true); setMsg(null);
    try {
      const res = await fetch(`${API_URL}/api/activation/udise-lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ udise_code: code }),
      });
      const d = await res.json();
      if (d.found && d.fields) {
        setForm((prev) => {
          const next = { ...prev };
          UDISE_SECTIONS.forEach((sec) => sec.fields.forEach((f) => {
            if (d.fields[f.key] !== undefined && d.fields[f.key] !== null && d.fields[f.key] !== '') {
              next[f.key] = toStr(d.fields[f.key]);
            }
          }));
          next.udise_code = code;
          return next;
        });
        setUdiseFields(new Set(d.fields.udise_imported_keys || Object.keys(d.fields)));
        setMsg({ ok: true, text: `✅ UDISE+ से ${(d.fields.udise_imported_keys || Object.keys(d.fields)).length} fields भर गए — verify करके Save करें।` });
      } else {
        setMsg({ ok: false, text: 'इस UDISE+ code की details नहीं मिलीं — details खुद भरें।' });
      }
    } catch { setMsg({ ok: false, text: 'Connection error' }); }
    setLooking(false);
  };

  const save = async () => {
    setSaving(true); setMsg(null);
    const payload = { ...form };
    ['medium_of_instruction'].forEach((k) => {
      if (payload[k]) payload[k] = payload[k].split(',').map((s) => s.trim()).filter(Boolean);
    });
    try {
      const res = await fetch(`${API_URL}/api/schools/${schoolId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(payload),
      });
      if (res.ok) setMsg({ ok: true, text: '✅ Profile saved successfully!' });
      else { const d = await res.json().catch(() => ({})); setMsg({ ok: false, text: (typeof d.detail === 'string' ? d.detail : 'Save failed') }); }
    } catch { setMsg({ ok: false, text: 'Connection error' }); }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="school360-profile-edit-tab">
      <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <p className="text-sm font-bold text-indigo-900">✏️ Verify & edit your school profile — in UDISE+ field order</p>
            <p className="text-xs text-indigo-700 mt-0.5">All fields appear in the same sequence as the government UDISE+ record. Imported data is pre-filled — just verify and save.</p>
          </div>
          <a
            href={`${API_URL}/api/bulk/template/school-profile/download`}
            download="school_profile_udise_template.csv"
            className="px-3 py-2 bg-white border border-indigo-200 text-indigo-700 rounded-lg text-xs font-semibold hover:bg-indigo-100 whitespace-nowrap"
            data-testid="download-school-profile-template"
          >
            <i className="fa-solid fa-download mr-1"></i> UDISE+ CSV Template
          </a>
        </div>
        {/* UDISE+ Code auto-fill */}
        <div className="flex flex-col sm:flex-row gap-2 bg-white rounded-lg p-3 border border-indigo-100">
          <div className="flex-1">
            <span className="text-[11px] font-semibold text-indigo-800">⚡ UDISE+ Code से एक-क्लिक auto-fill</span>
            <input
              value={udiseInput}
              onChange={(e) => setUdiseInput(e.target.value.replace(/\D/g, '').slice(0, 11))}
              placeholder="11-digit UDISE+ code (e.g. 23260102260)"
              inputMode="numeric"
              className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 outline-none font-mono tracking-wide"
              data-testid="udise-autofill-input"
            />
          </div>
          <button
            onClick={autofill}
            disabled={looking}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700 disabled:opacity-50 self-end whitespace-nowrap"
            data-testid="udise-autofill-btn"
          >
            {looking ? 'खोज रहे…' : '⚡ Auto-fill'}
          </button>
        </div>
      </div>

      {(() => {
        const all = UDISE_SECTIONS.flatMap((s) => s.fields.map((f) => f.key));
        const udiseCount = all.filter((k) => udiseFields.has(k)).length;
        const filled = all.filter((k) => String(form[k] ?? '').trim()).length;
        return (
          <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="udise-provenance-summary">
            <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 font-semibold">🏛️ UDISE+ से: {udiseCount}</span>
            <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold">✏️ भरे हुए: {filled}/{all.length}</span>
            <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 font-semibold">⚠️ खाली: {all.length - filled}</span>
            <span className="text-slate-400">— हरे <b className="text-emerald-600">UDISE+</b> badge वाले fields सरकारी record से आए हैं।</span>
          </div>
        );
      })()}

      {UDISE_SECTIONS.map((sec) => (
        <Section key={sec.title} title={sec.title} testid={`profile-section-${sec.title}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {sec.fields.map((f) => {
              const fromUdise = udiseFields.has(f.key);
              const empty = !String(form[f.key] ?? '').trim();
              return (
              <label key={f.key} className="block">
                <span className="text-[11px] text-slate-500 flex items-center gap-1.5">
                  {f.label}
                  {fromUdise && <span className="text-[9px] font-bold px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full" data-testid={`udise-badge-${f.key}`}>UDISE+</span>}
                  {!fromUdise && empty && f.key !== 'udise_code' && <span className="text-[9px] font-bold px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full">भरें</span>}
                </span>
                <input
                  type={f.type || 'text'}
                  value={form[f.key] ?? ''}
                  onChange={(e) => { upd(f.key, e.target.value); if (fromUdise) setUdiseFields((s) => { const n = new Set(s); n.delete(f.key); return n; }); }}
                  readOnly={f.key === 'udise_code'}
                  className={`w-full mt-0.5 px-3 py-2 text-sm rounded-lg border outline-none focus:ring-1 ${f.key === 'udise_code' ? 'bg-slate-50 text-slate-500 border-slate-200' : fromUdise ? 'border-emerald-300 bg-emerald-50/40 focus:border-emerald-500 focus:ring-emerald-200' : empty ? 'border-amber-200 focus:border-amber-400 focus:ring-amber-100' : 'border-slate-200 focus:border-indigo-500 focus:ring-indigo-200'}`}
                  data-testid={`profile-field-${f.key}`}
                />
              </label>
            );})}
          </div>
        </Section>
      ))}

      {msg && (
        <div className={`text-sm px-4 py-2.5 rounded-lg ${msg.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`} data-testid="profile-save-msg">{msg.text}</div>
      )}
      <div className="sticky bottom-0 bg-white pt-3 border-t border-slate-100">
        <button onClick={save} disabled={saving} className="w-full py-3 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 disabled:opacity-50" data-testid="profile-save-btn">
          {saving ? 'Saving…' : '💾 Save Profile'}
        </button>
      </div>
    </div>
  );
};


/* ---------------- Main ---------------- */
export default function SchoolProfile360({ schoolId, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('overview');
  const authToken = token || localStorage.getItem('token');

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        const res = await fetch(`${API_URL}/api/schools/${schoolId}/profile-360`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!res.ok) throw new Error('लोड नहीं हो पाया');
        const d = await res.json();
        if (active) setData(d);
      } catch (e) {
        if (active) setError(e.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [schoolId, authToken]);

  if (loading) return <div className="p-6 text-center text-slate-400" data-testid="school360-loading">⏳ School profile लोड हो रहा है...</div>;
  if (error) return <div className="p-6 text-center text-red-500" data-testid="school360-error">⚠️ {error}</div>;
  if (!data) return null;

  const { school = {}, udise = {}, counts = {}, hierarchy = [] } = data;

  const tabs = [
    { id: 'overview', label: '📋 Overview' },
    { id: 'edit', label: '✏️ Profile' },
    { id: 'faculty', label: `👨‍🏫 Faculty (${counts.teachers ?? 0})` },
    { id: 'students', label: `🎓 Students (${counts.students ?? 0})` },
    { id: 'activity', label: '🕑 Activity' },
  ];

  return (
    <div className="space-y-4" data-testid="school-profile-360">
      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl p-5 text-white">
        <p className="text-lg font-bold">{school.name || udise.school_name || 'School'}</p>
        <p className="text-xs opacity-80 mt-0.5">
          {[udise.district, udise.state, udise.udise_code && `UDISE: ${udise.udise_code}`].filter(Boolean).join(' · ') || school.phone}
        </p>
      </div>

      <div className="flex gap-2 bg-white rounded-xl shadow-sm p-1.5" data-testid="school360-tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 text-xs sm:text-sm font-semibold py-2 rounded-lg transition-colors ${tab === t.id ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
            data-testid={`school360-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab data={data} />}
      {tab === 'edit' && <ProfileEditTab schoolId={schoolId} authToken={authToken} school={school} udise={udise} />}
      {tab === 'faculty' && <FacultyTab schoolId={schoolId} authToken={authToken} />}
      {tab === 'students' && <StudentsTab schoolId={schoolId} authToken={authToken} hierarchy={hierarchy} />}
      {tab === 'activity' && <ActivityTab schoolId={schoolId} authToken={authToken} />}
    </div>
  );
}
